/**
 * Cart Drawer JavaScript
 * Handles all cart drawer functionality including opening, closing, and cart updates
 */

class CartDrawer {
  constructor() {
    this.drawer = document.querySelector('#cart-drawer');
    this.overlay = null;
    this.closeBtn = null;
    this.loading = document.querySelector('.cart-drawer__loading');
    this.isOpen = false;
    this.isUpdating = false;

    if (this.drawer) {
      this.init();
    }
  }

  init() {
    this.updateReferences();
    this.bindEvents();
    this.updateCartCount();
    this.setupFormOverride();
  }

  updateReferences() {
    this.overlay = this.drawer?.querySelector('.cart-drawer__overlay');
    this.closeBtn = this.drawer?.querySelector('.cart-drawer__close');
  }

  setupFormOverride() {
    // Simple approach: listen for form submissions and check if it's a cart drawer form
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (form.classList.contains('feedback-add_in_drawer') || 
          (form.classList.contains('product-purchase-form') && this.isCartDrawerEnabled())) {
        e.preventDefault();
        e.stopPropagation();
        this.handleFormSubmit(form);
      }
    }, true); // Use capture phase to catch before other handlers
  }

  isCartDrawerEnabled() {
    // Check if any forms have the cart drawer class or if we should fallback
    return document.querySelectorAll('.feedback-add_in_drawer').length > 0 ||
           window.location.search.includes('cart_drawer=true'); // URL parameter fallback
  }

  bindEvents() {
    // Cart icon clicks - prevent navigation and open drawer
    document.addEventListener('click', (e) => {
      const cartLink = e.target.closest('.cart');
      if (cartLink && !e.target.closest('.text-link')) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.open();
        return false;
      }
    }, true); // Use capture phase to intercept before other handlers

    // Also prevent any existing cart link navigation
    const cartLinks = document.querySelectorAll('.cart');
    cartLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        if (!e.target.closest('.text-link')) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          this.open();
          return false;
        }
      }, true);
    });

    // Close drawer events
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }

    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // Escape key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    // Quantity controls
    document.addEventListener('click', (e) => {
      if (e.target.closest('.cart-drawer__quantity-btn')) {
        e.preventDefault();
        this.handleQuantityChange(e.target.closest('.cart-drawer__quantity-btn'));
      }

      if (e.target.closest('.cart-drawer__item-remove')) {
        e.preventDefault();
        this.removeItem(e.target.closest('.cart-drawer__item-remove'));
      }
    });

    // Quantity input changes
    document.addEventListener('change', (e) => {
      if (e.target.classList.contains('cart-drawer__quantity-input')) {
        this.handleQuantityInputChange(e.target);
      }
    });

    // Cart note changes
    document.addEventListener('change', (e) => {
      if (e.target.id === 'cart-drawer-note') {
        this.updateCartNote(e.target.value);
      }
    });

    // Checkout button
    document.addEventListener('click', (e) => {
      if (e.target.hasAttribute('data-checkout-button')) {
        e.preventDefault();
        this.checkout();
      }
    });
  }

  async handleFormSubmit(form) {
    const formData = new FormData(form);
    const submitButton = form.querySelector('[type="submit"]');
    const originalText = submitButton?.textContent || 'Add to Cart';

    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = 'Adding...';
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || errorData.message || 'Failed to add to cart');
      }

      const item = await response.json();

      // Success feedback
      if (submitButton) {
        submitButton.textContent = 'Added!';
      }
      
      // Open cart drawer and refresh content
      this.open();
      this.refreshDrawerContent();

      setTimeout(() => {
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.textContent = originalText;
        }
      }, 2000);

    } catch (error) {
      console.error('Add to cart failed:', error);
      
      // Show error message in form if available
      const errorContainer = form.querySelector('.error-message');
      if (errorContainer) {
        errorContainer.textContent = error.message;
        errorContainer.style.display = 'block';
        
        setTimeout(() => {
          errorContainer.style.display = 'none';
        }, 5000);
      }
      
      // Error feedback on button
      if (submitButton) {
        submitButton.textContent = 'Error - Try Again';
        setTimeout(() => {
          submitButton.disabled = false;
          submitButton.textContent = originalText;
        }, 2000);
      }
    }
  }

  open() {
    if (!this.drawer || this.isOpen) return;

    this.isOpen = true;
    this.drawer.classList.add('cart-drawer--open');
    document.body.classList.add('cart-drawer-open');
    
    // Refresh content when opening
    this.refreshDrawerContent();
  }

  close() {
    if (!this.drawer || !this.isOpen) return;

    this.isOpen = false;
    this.drawer.classList.remove('cart-drawer--open');
    document.body.classList.remove('cart-drawer-open');
  }

  async refreshDrawerContent() {
    if (!this.drawer) return;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Update cart count in header
      this.updateCartCount(cart.item_count);
      
      // Get fresh cart drawer HTML by reloading the section
      const drawerResponse = await fetch('/?section_id=cart-drawer');
      const sectionHtml = await drawerResponse.text();
      
      if (sectionHtml) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = sectionHtml;
        const newDrawer = tempDiv.querySelector('#cart-drawer');
        
        if (newDrawer && this.drawer) {
          // Replace drawer content
          this.drawer.innerHTML = newDrawer.innerHTML;
          
          // Update references
          this.updateReferences();
          
          // Rebind close events for new elements
          if (this.closeBtn) {
            this.closeBtn.addEventListener('click', () => this.close());
          }
          
          if (this.overlay) {
            this.overlay.addEventListener('click', () => this.close());
          }
        }
      }
    } catch (error) {
      console.error('Failed to refresh cart drawer:', error);
    }
  }

  async handleQuantityChange(button) {
    if (this.isUpdating) return;

    const action = button.dataset.action;
    const key = button.dataset.key;
    const item = button.closest('.cart-drawer__item');
    const input = item?.querySelector('.cart-drawer__quantity-input');
    const currentQuantity = parseInt(input?.value || 0);

    let newQuantity = currentQuantity;
    if (action === 'increase') {
      newQuantity = currentQuantity + 1;
    } else if (action === 'decrease') {
      newQuantity = Math.max(0, currentQuantity - 1);
    }

    await this.updateQuantity(key, newQuantity, item);
  }

  async handleQuantityInputChange(input) {
    if (this.isUpdating) return;

    const key = input.dataset.key;
    const newQuantity = parseInt(input.value) || 0;
    const item = input.closest('.cart-drawer__item');

    await this.updateQuantity(key, newQuantity, item);
  }

  async removeItem(button) {
    if (this.isUpdating) return;

    const key = button.dataset.key;
    const item = button.closest('.cart-drawer__item');

    await this.updateQuantity(key, 0, item);
  }

  async updateQuantity(key, quantity, itemElement) {
    if (this.isUpdating || !itemElement) return;

    this.isUpdating = true;
    itemElement.classList.add('cart-drawer__item--updating');

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: key,
          quantity: quantity
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update cart');
      }

      const cart = await response.json();
      
      // Refresh drawer content
      await this.refreshDrawerContent();

      // Close drawer if cart is empty
      if (cart.item_count === 0) {
        setTimeout(() => {
          this.close();
        }, 500);
      }

    } catch (error) {
      console.error('Failed to update cart:', error);
      
      // Revert input value on error
      const input = itemElement.querySelector('.cart-drawer__quantity-input');
      if (input) {
        input.value = input.dataset.initialValue;
      }
    } finally {
      this.isUpdating = false;
      itemElement.classList.remove('cart-drawer__item--updating');
    }
  }

  async updateCartNote(note) {
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          note: note
        })
      });
    } catch (error) {
      console.error('Failed to update cart note:', error);
    }
  }

  updateCartCount(count = null) {
    const cartCountElements = document.querySelectorAll('.cart div');
    const drawerCount = document.querySelector('.cart-drawer__count');
    
    if (count === null) {
      // Fetch current cart count
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          cartCountElements.forEach(el => {
            el.textContent = cart.item_count;
          });
          if (drawerCount) {
            drawerCount.textContent = `(${cart.item_count})`;
          }
        })
        .catch(error => {
          console.error('Failed to fetch cart count:', error);
        });
    } else {
      cartCountElements.forEach(el => {
        el.textContent = count;
      });
      if (drawerCount) {
        drawerCount.textContent = `(${count})`;
      }
    }
  }

  checkout() {
    window.location.href = '/checkout';
  }
}

// Initialize cart drawer when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  try {
    window.cartDrawer = new CartDrawer();
    console.log('Cart drawer initialized successfully');
    
    // Add test function for debugging
    window.testCartDrawer = () => {
      console.log('Testing cart drawer...');
      if (window.cartDrawer) {
        window.cartDrawer.open();
      } else {
        console.log('Cart drawer not initialized');
      }
    };
    
  } catch (error) {
    console.error('Failed to initialize cart drawer:', error);
  }
});

// Export for use in other scripts
window.CartDrawer = CartDrawer; 